<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import IntegrationConnectionCard from '../shared/IntegrationConnectionCard.vue';
import DrivePickerModal from './components/DrivePickerModal.vue';

const PROVIDERS = {
  google: {
    icon: 'i-woot-google-drive',
    authorize: 'drives/authorizeGoogle',
    needsChannelOption: true,
  },
  onedrive: {
    icon: 'i-woot-sharepoint',
    authorize: 'drives/authorizeOnedrive',
    needsChannelOption: true,
  },
  sharepoint: {
    icon: 'i-woot-sharepoint',
    authorize: 'drives/authorizeSharepoint',
    needsSiteUrl: true,
  },
  dropbox: {
    icon: 'i-woot-dropbox',
    authorize: 'drives/authorizeDropbox',
  },
};

const route = useRoute();
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const provider = computed(() => route.params.provider);
const providerConfig = computed(() => PROVIDERS[provider.value] || null);

const uiFlags = computed(() => getters['drives/getUIFlags'].value || {});
const drive = computed(
  () => getters['drives/getDriveByProvider'].value(provider.value) || null
);

const isLoading = computed(() => uiFlags.value.isFetching);
const isConnected = computed(() => !!drive.value);
const isConnecting = ref(false);
const useChannel = ref(false);
const sharepointSiteUrl = ref('');
const showPickerModal = ref(false);
const channelInfo = ref(null);

const providerLabel = computed(() =>
  t(`INTEGRATION_SETTINGS.DRIVES.PROVIDERS.${provider.value?.toUpperCase()}`)
);

onMounted(async () => {
  await store.dispatch('drives/get');
  if (providerConfig.value?.needsChannelOption) {
    try {
      const action =
        provider.value === 'google'
          ? 'drives/checkGoogleChannel'
          : 'drives/checkMicrosoftChannel';
      channelInfo.value = await store.dispatch(action);
    } catch (error) {
      channelInfo.value = null;
    }
  }
});

const handleConnect = async () => {
  if (!providerConfig.value) return;

  if (providerConfig.value.needsSiteUrl && !sharepointSiteUrl.value) {
    useAlert(t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.SHAREPOINT_URL_REQUIRED'));
    return;
  }

  isConnecting.value = true;
  try {
    let response;
    if (provider.value === 'sharepoint') {
      response = await store.dispatch(
        providerConfig.value.authorize,
        sharepointSiteUrl.value
      );
    } else if (provider.value === 'dropbox') {
      response = await store.dispatch(providerConfig.value.authorize);
    } else {
      response = await store.dispatch(
        providerConfig.value.authorize,
        useChannel.value
      );
    }
    if (response.authorization_url) {
      window.location.href = response.authorization_url;
    }
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.DRIVES.ADD_MODAL.ERROR')
    );
    isConnecting.value = false;
  }
};

const handleDisconnect = async () => {
  if (!drive.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTEGRATION_SETTINGS.DRIVES.DELETE.CONFIRM'))) return;
  try {
    await store.dispatch('drives/delete', drive.value.id);
    useAlert(t('INTEGRATION_SETTINGS.DRIVES.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('INTEGRATION_SETTINGS.DRIVES.DELETE.ERROR'));
  }
};

const handleTestConnection = async () => {
  try {
    await store.dispatch('drives/testConnection', drive.value.id);
    useAlert(t('INTEGRATION_SETTINGS.DRIVES.TEST_CONNECTION.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.DRIVES.TEST_CONNECTION.ERROR')
    );
  }
};

const handleReauthorize = async () => {
  try {
    const response = await store.dispatch('drives/reauthorize', drive.value.id);
    if (response.authorization_url) {
      window.location.href = response.authorization_url;
    }
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.DRIVES.REAUTHORIZE.ERROR')
    );
  }
};

const openPicker = async () => {
  if (provider.value === 'google') {
    try {
      const { useGooglePicker } = await import(
        'dashboard/composables/useGooglePicker'
      );
      const picker = useGooglePicker();
      await picker.buildAndShowPickerInPlace({
        driveId: drive.value.id,
        onSelect: () => {},
        onCancel: () => {},
      });
    } catch (err) {
      useAlert(
        err?.message || t('INTEGRATION_SETTINGS.DRIVES.VIEW_FILES_ERROR')
      );
    }
    return;
  }
  showPickerModal.value = true;
};
</script>

<template>
  <SettingsLayout
    v-if="providerConfig"
    :header-title="providerLabel"
    :button-label="
      isConnected
        ? $t('INTEGRATION_SETTINGS.DRIVES.DISCONNECT_BUTTON')
        : $t('INTEGRATION_SETTINGS.DRIVES.CONNECT_BUTTON')
    "
    @click="isConnected ? handleDisconnect() : handleConnect()"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <template v-else-if="!isConnected">
      <IntegrationEmptyState
        :icon="providerConfig.icon"
        :title="
          $t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.EMPTY_TITLE', {
            provider: providerLabel,
          })
        "
        :description="
          $t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.EMPTY_DESCRIPTION', {
            provider: providerLabel,
          })
        "
        :button-label="$t('INTEGRATION_SETTINGS.DRIVES.CONNECT_BUTTON')"
        @action="handleConnect"
      />

      <div
        v-if="providerConfig.needsSiteUrl"
        class="mx-auto mt-6 max-w-md space-y-2"
      >
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.SHAREPOINT_DOMAIN') }}
        </label>
        <input
          v-model="sharepointSiteUrl"
          type="url"
          placeholder="https://yourcompany.sharepoint.com"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12"
        />
      </div>

      <div
        v-if="providerConfig.needsChannelOption && channelInfo?.available"
        class="mx-auto mt-4 flex max-w-md items-center gap-2"
      >
        <input id="use-channel" v-model="useChannel" type="checkbox" />
        <label for="use-channel" class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.USE_CHANNEL') }}
        </label>
      </div>

      <div v-if="isConnecting" class="mt-4 flex justify-center">
        <Spinner />
      </div>
    </template>

    <div v-else class="space-y-4">
      <IntegrationConnectionCard
        :icon="providerConfig.icon"
        :title="drive.name || providerLabel"
        :description="
          $t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.CONNECTED_DESCRIPTION', {
            provider: providerLabel,
          })
        "
      >
        <template #actions>
          <Button
            slate
            faded
            size="sm"
            :label="$t('INTEGRATION_SETTINGS.DRIVES.TEST_CONNECTION.BUTTON')"
            @click="handleTestConnection"
          />
          <Button
            slate
            faded
            size="sm"
            :label="$t('INTEGRATION_SETTINGS.DRIVES.REAUTHORIZE.BUTTON')"
            @click="handleReauthorize"
          />
          <Button
            v-if="drive.status === 'active'"
            slate
            faded
            size="sm"
            :label="$t('INTEGRATION_SETTINGS.DRIVES.VIEW_FILES')"
            @click="openPicker"
          />
        </template>
      </IntegrationConnectionCard>
    </div>

    <DrivePickerModal
      v-model:show="showPickerModal"
      :drive-id="drive?.id"
      :viewer-mode="true"
      @cancel="showPickerModal = false"
    />
  </SettingsLayout>

  <div v-else class="flex items-center justify-center py-12 text-n-slate-11">
    {{ $t('INTEGRATION_SETTINGS.DRIVES.PROVIDER_PAGE.UNKNOWN') }}
  </div>
</template>
